import { reportsControlStyles } from '@/common/components/Select/styles';
import SelectController from '@/common/components/SelectController';
import TextField from '@/common/components/TextField';
import { DatePicker } from '@/common/components/ui/datePicker';
import { useCities } from '@/common/hooks/queries/useCities';
import { useProducts } from '@/common/hooks/queries/useProducts';
import useDebounce from '@/common/hooks/useDebounce';
import { GeoTransformers } from '@/common/services/geoTransformers';
import { Mapper } from '@/common/services/mapper';
import { ProductData } from '@/common/types/product.type';
import { filterLabel } from '@/features/reports/constants/filterLabel.constant';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PermittedReportFilterData } from '../types/permittedReports.type';
import { CompanyBranchData } from '@/common/types/companyBranches.type';
import Checkbox from '@/common/components/Checkbox';
import { useAvailableCompanyBranches } from '@/features/companyBranches/hooks/useAvailableCompanyBranches';
import { useEquipments } from '@/features/equipments/hooks/useEquipments';
import { EquipmentsTransformer } from '@/features/equipments/services/equipmentTransformer';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { useEquipmentCategories } from '@/features/equipments/hooks/useEquipmentCategories';
import { EquipmentCategoryData } from '@/features/equipments/types/equipments.type';
import { equipmentStatusOptions } from '@/common/constants/equipmentStatus.constants';
import { options as propertyKindOptions } from "@/features/equipments/constants/propertyKindOptions.constant"
import { options as usageKindOptions } from "@/features/equipments/constants/usageKindOptions.constant"

interface FilterDefaultFieldType {
  [key: string]: (props: {
    control: Control;
    errors: FieldErrors;
    filterName: string;
    value: string;
    setFilterParams: (mutationFn: (searchParams: any) => any) => void;
    filterProps: PermittedReportFilterData;
  }) => React.ReactNode;
}

export const defaultReportsFilterFields: FilterDefaultFieldType = {
  textField: ({ control, filterName, value, setFilterParams }) => (
    <Controller
      control={control}
      name={filterName}
      render={({ field: { onChange, ...rest } }) => (
        <TextField
          {...rest}
          label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
          className="w-full"
          value={value}
          onChange={(event: any) => {
            onChange(event);
            setFilterParams((searchParams) => ({
              ...searchParams,
              q: { ...searchParams.q, [filterName]: event.target.value },
            }));
          }}
        />
      )}
    />
  ),
  numberField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <Controller
        control={control}
        name={filterName}
        render={({ field: { onChange, ...rest } }) => (
          <TextField
            {...rest}
            label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
            className="w-full"
            type="number"
            value={value}
            markAsRequired={filterProps.required}
            onChange={(event: any) => {
              onChange(event);
              setFilterParams((searchParams) => ({
                ...searchParams,
                q: { ...searchParams.q, [filterName]: event.target.value },
              }));
            }}
            errorMessage={errors[filterName]?.message as string | undefined || undefined}
          />
        )}
      />
    );
  },
  dateField: ({ control, filterName, value, setFilterParams }) => (
    <Controller
      control={control}
      name={filterName}
      render={({ field }) => (
        <DatePicker
          name={filterName}
          label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
          date={!!value ? new Date(value) : undefined}
          setDate={(value) => {
            field.onChange(value);
            setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: value } }));
          }}
          variant="default"
          className="w-full"
          inputClassName="h-[52px] text-sm rounded-md rounded-r-none bg-neutral-op-8 hover:bg-neutral-op-8"
        />
      )}
    />
  ),
  citySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCity, setSearchCity] = useDebounce('');

    const { data: citiesOptions, isFetching: citiesIsFetching } = useCities({
      filters: {
        search: searchCity,
      },
      mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCity}
        options={citiesOptions}
        isLoading={citiesIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  productSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCity, setSearchCity] = useDebounce('');

    const { data: productsOptions, isFetching: productsIsFetching } = useProducts({
      filters: {
        search: searchCity,
      },
      mapper: Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCity}
        options={productsOptions}
        isLoading={productsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  equipmentCategorySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('');

    const { 
      data: equipmentCategoryOptions,
      isFetching: equipmentCategoryOptionsIsFetching
     } = useEquipmentCategories({
      filters: { search: searchEquipmentCategory },
      mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
    })

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchEquipmentCategory}
        options={equipmentCategoryOptions}
        isLoading={equipmentCategoryOptionsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  equipmentStatusSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        options={equipmentStatusOptions}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  equipmentUsageKindSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        options={usageKindOptions}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  equipmentPropertyKindSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        options={propertyKindOptions}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  clientCompanyBranchSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('');

    const { data: companiesOptions, isFetching: companyIsFetching } = useAvailableCompanyBranches({
      filters: {
        search: searchCompanyBranch
      },
      mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCompanyBranch}
        options={companiesOptions as { value: string, label: string }[]}
        isLoading={companyIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  checkbox: ({ control, filterName, value, setFilterParams }) => (
    <Controller
      control={control}
      name={filterName}
      render={({ field: { onChange, ...rest } }) => (
        <Checkbox
          {...rest}
          label={filterLabel[(filterName as keyof typeof filterLabel) || 'default'] || filterName}
          checked={!!value}
          onChange={(event) => {
            onChange(event);
            setFilterParams((searchParams) => ({
              ...searchParams,
              q: { ...searchParams.q, [filterName]: event.currentTarget.checked || null },
            }));
          }}
        />
      )}
    />
  ),
  clientEquipmentSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const { filterParams } = useFilterParams();

    const [searchEquipment, setSearchEquipment] = useDebounce('');

    const {
      data: equipmentsOptions,
      isFetching: equipmentsIsFetching,
    } = useEquipments({
      filters: {
        search: searchEquipment,
        branchId: filterParams?.q?.client_branch_id_eq?.value,
      },
      mapper: Mapper.mapWithTransformer({
        transformer: EquipmentsTransformer.optionsTransformer(),
      }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchEquipment}
        options={equipmentsOptions}
        isLoading={equipmentsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
};
